import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Tooltip,
  Rectangle,
} from 'recharts';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import { isUndefined } from 'lodash';
import pluralize from 'pluralize';

import { summaryTimesheetStatusReportDataSelector } from 'redux/selectors/homeDashboardSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './TimesheetStatusReportCard.module.less';

type FormattedChartData = {
  name: string;
  value: number;
  totalCount: number;
  fill: string;
  fillColor: string;
}[];

const renderCustomizedLabel = (props: any) => {
  const { x, y, width, value } = props;

  return (
    <g>
      <text
        x={x + width / 2}
        y={y - 7}
        fill="black"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  );
};

const renderXAxisTick = (props: any, data: FormattedChartData) => {
  const { x, y, width, visibleTicksCount, height, index, payload } = props;
  const dataItem = data[index];

  return (
    <g>
      <foreignObject
        x={x - payload.offset}
        y={y}
        width={width / visibleTicksCount}
        height={height}
      >
        <div className={styles.caption}>
          <span
            className={styles.captionDot}
            style={{ background: dataItem.fillColor }}
          />
          {dataItem.name}
        </div>
      </foreignObject>
    </g>
  );
};

const renderTooltip = (props: any) => {
  if (props.active) {
    const { fillColor, name, totalCount } = props.payload[0].payload;

    return (
      <div className={styles.tooltip}>
        <span
          className={styles.colorBox}
          style={{
            backgroundColor: fillColor,
          }}
        />
        <span>{`${name}: ${totalCount} active ${pluralize(
          'entry',
          totalCount,
        )}`}</span>
      </div>
    );
  }

  return null;
};

const renderShape = (props: any, activeTooltipIndex?: number) => {
  const { fill, x, y, width, height, radius, index } = props;
  const isActive = index === activeTooltipIndex;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      filter={isActive ? 'url(#timesheetBarShadow)' : undefined}
      radius={radius}
      stroke="#000000"
      strokeWidth={isActive ? '0.1' : '0'}
    />
  );
};

export default function TimesheetStatusReportCard() {
  const summaryTimesheetStatusReportData = useAppSelector(
    summaryTimesheetStatusReportDataSelector,
  );
  const notSubmittedEntities =
    summaryTimesheetStatusReportData?.notSubmittedAssignmentTotalCount || 0;
  const notApprovedEntities =
    summaryTimesheetStatusReportData?.notApprovedAssignmentTotalCount || 0;
  const approvedEntities =
    summaryTimesheetStatusReportData?.approvedAssignmentTotalCount || 0;
  const isDisable =
    !notSubmittedEntities && !notApprovedEntities && !approvedEntities;
  const formattedChartData: FormattedChartData = useMemo(
    () => [
      {
        name: 'Not Submitted',
        value:
          summaryTimesheetStatusReportData?.notSubmittedAssignmentPercentOfTotal ||
          0,
        totalCount:
          summaryTimesheetStatusReportData?.notSubmittedAssignmentTotalCount ||
          0,
        fill: !isDisable ? 'url(#gradNotSubmitted)' : '#D8D8D8',
        fillColor: '#FF8178',
      },
      {
        name: 'Not Approved',
        value:
          summaryTimesheetStatusReportData?.notApprovedAssignmentPercentOfTotal ||
          0,
        totalCount:
          summaryTimesheetStatusReportData?.notApprovedAssignmentTotalCount ||
          0,
        fill: !isDisable ? 'url(#gradNotApproved)' : '#D8D8D8',
        fillColor: '#FFD78A',
      },
      {
        name: 'Approved',
        value:
          summaryTimesheetStatusReportData?.approvedAssignmentPercentOfTotal ||
          0,
        totalCount:
          summaryTimesheetStatusReportData?.approvedAssignmentTotalCount || 0,
        fill: !isDisable ? 'url(#gradApproved)' : '#D8D8D8',
        fillColor: '#65BB6A',
      },
    ],
    [summaryTimesheetStatusReportData, isDisable],
  );
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number>();

  return (
    <div className={styles.chartWrapper}>
      <Row gutter={[4, 4]}>
        <Col xs={14} className="d-flex">
          <div className={styles.border}>
            <div className={styles.title}>
              <Link to="/timesheet-status-report-details">
                Timesheet Status Report
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={252}>
              <BarChart
                data={formattedChartData}
                margin={{ top: 20 }}
                barSize={30}
              >
                <defs>
                  <linearGradient
                    id="gradNotSubmitted"
                    x1="100%"
                    y1="0"
                    x2="0"
                    y2="0"
                    spreadMethod="reflect"
                  >
                    <stop offset="0" stopColor="#FFA8A1" />
                    <stop offset="1" stopColor="#FF8178" />
                  </linearGradient>
                  <linearGradient
                    id="gradNotApproved"
                    x1="100%"
                    y1="0"
                    x2="0"
                    y2="0"
                    spreadMethod="reflect"
                  >
                    <stop offset="0" stopColor="#FFE7BA" />
                    <stop offset="1" stopColor="#FFD78A" />
                  </linearGradient>
                  <linearGradient
                    id="gradApproved"
                    x1="100%"
                    y1="0"
                    x2="0"
                    y2="0"
                    spreadMethod="reflect"
                  >
                    <stop offset="0" stopColor="#A5D6A7" />
                    <stop offset="1" stopColor="#65BB6A" />
                  </linearGradient>
                  <filter
                    id="timesheetBarShadow"
                    x="0"
                    y="0"
                    width="150%"
                    height="150%"
                    filterUnits="userSpaceOnUse"
                  >
                    <feDropShadow
                      dx="0"
                      dy="0"
                      stdDeviation="3"
                      floodColor="#141e24"
                    />
                  </filter>
                </defs>
                {!isDisable && !isUndefined(activeTooltipIndex) && (
                  <Tooltip
                    content={renderTooltip}
                    cursor={false}
                    animationDuration={400}
                  />
                )}
                <CartesianGrid stroke="#f5f5f5" vertical={false} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={50}
                  domain={[0, 100]}
                  unit=" %"
                />
                <Bar
                  dataKey="value"
                  radius={[2, 2, 0, 0]}
                  minPointSize={3}
                  shape={(props) => renderShape(props, activeTooltipIndex)}
                  onMouseEnter={(data: any, index: number) => {
                    !isDisable && setActiveTooltipIndex(index);
                  }}
                  onMouseLeave={() => setActiveTooltipIndex(undefined)}
                >
                  <LabelList
                    dataKey="totalCount"
                    position="top"
                    content={renderCustomizedLabel}
                  />
                </Bar>
                <XAxis
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={(props) => renderXAxisTick(props, formattedChartData)}
                  height={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>

        <Col xs={10} className="d-flex">
          <div className={styles.legendBox}>
            <div className={styles.legendWrapper}>
              <span
                className={styles.legendBorder}
                style={{
                  background: formattedChartData[0].fillColor,
                }}
              />
              <div
                data-id="open"
                className={styles.legendCount}
                style={{
                  color: formattedChartData[0].fillColor,
                }}
              >
                {formattedChartData[0].totalCount}
              </div>
              <div className={styles.legendHeader}>
                <span
                  className={styles.dot}
                  style={{
                    background: formattedChartData[0].fillColor,
                  }}
                />
                <span>Not Submitted</span>
              </div>
            </div>
            <div className={styles.legendWrapper}>
              <span
                className={styles.legendBorder}
                style={{
                  background: formattedChartData[1].fillColor,
                }}
              />
              <div
                data-id="submitted"
                className={styles.legendCount}
                style={{
                  color: formattedChartData[1].fillColor,
                }}
              >
                {formattedChartData[1].totalCount}
              </div>
              <div className={styles.legendHeader}>
                <span
                  className={styles.dot}
                  style={{
                    background: formattedChartData[1].fillColor,
                  }}
                />
                <span>Not Approved</span>
              </div>
            </div>
            <div className={styles.legendWrapper}>
              <span
                className={styles.legendBorder}
                style={{
                  background: formattedChartData[2].fillColor,
                }}
              />
              <div
                data-id="approved"
                className={styles.legendCount}
                style={{
                  color: formattedChartData[2].fillColor,
                }}
              >
                {formattedChartData[2].totalCount}
              </div>
              <div className={styles.legendHeader}>
                <span
                  className={styles.dot}
                  style={{
                    background: formattedChartData[2].fillColor,
                  }}
                />
                <span>Approved</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
